import React from 'react';
import {
  Card,
  Typography,
  Input,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Tabs,
  Select,
  DatePicker,
  TimePicker,
  Empty,
  TextArea
} from '@douyinfe/semi-ui';

import {
  IconAIStrokedLevel1,
  IconImageStroked,
  IconCloudUploadStroked,
  IconSendStroked,
  IconSettingStroked,
  IconClockStroked,
  IconCheckCircleStroked,
  IconHelpCircleStroked
} from '@douyinfe/semi-icons';

const { Text } = Typography;
const { TabPane } = Tabs;

const PROFILE_PLATFORMS = [
  'Instagram',
  'Telegram',
  'VK',
  'FanVue',
  'DeviantArt',
  'YouTube',
  'X / Twitter'
];

const AI_MODEL_OPTIONS = {
  text: ['GPT-4o', 'GPT-4.1', 'Claude 3.5', 'Gemini 1.5', 'Qwen Max'],
  image: ['DALL-E 3', 'Midjourney', 'Stable Diffusion XL', 'Flux', 'Leonardo'],
  voice: ['ElevenLabs', 'OpenAI Voice', 'PlayHT', 'Azure Voice'],
  optimize: ['GPT-4o', 'Claude 3.5', 'Gemini 1.5', 'Qwen Max']
};

const OptionTiles = ({ label, value, options, onChange }) => (
  <div className="factory-option-block">
    <Text className="factory-label">{label}</Text>

    <div className="factory-option-grid">
      {options.map(option => (
        <Button
          key={option}
          className={value === option ? 'factory-option-tile active' : 'factory-option-tile'}
          onClick={() => onChange(option)}
        >
          {option}
        </Button>
      ))}
    </div>
  </div>
);

const IncludeTiles = ({ values, onToggle }) => {
  const options = ['Hashtags', 'Emojis', 'CTA', 'References'];

  return (
    <div className="factory-option-block">
      <Text className="factory-label">Include</Text>

      <div className="factory-include-grid">
        {options.map(option => (
          <button
            key={option}
            type="button"
            className={values.includes(option) ? 'factory-include-tile active' : 'factory-include-tile'}
            onClick={() => onToggle(option)}
          >
            {values.includes(option) ? '✓' : ''}
            <span>{option}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const EngineRow = ({ id, engine, models, onToggle, onModelChange }) => {
  const active = engine.enabled;

  return (
    <div className={active ? 'factory-engine-row active' : 'factory-engine-row disabled'}>
      <button
        type="button"
        className={active ? 'factory-engine-switch on' : 'factory-engine-switch'}
        onClick={() => onToggle(id)}
      >
        {active ? 'ON' : 'OFF'}
      </button>

      <div className="factory-engine-main">
        <strong>{engine.label}</strong>
        <span>{active ? 'Included in production pipeline' : 'Skipped during generation'}</span>
      </div>

      <Select
        value={engine.model}
        disabled={!active}
        onChange={(v) => onModelChange(id, v)}
        className="factory-engine-select"
        style={{ width: '100%' }}
      >
        {models.map(model => (
          <Select.Option key={model} value={model}>
            {model}
          </Select.Option>
        ))}
      </Select>

      <Tag className={active ? 'tag-custom tag-green' : 'tag-custom tag-amber'}>
        {active ? engine.status : 'Skipped'}
      </Tag>
    </div>
  );
};

const PipelineProgress = ({ engines, activeStep }) => {
  const steps = Object.entries(engines)
    .filter(([, engine]) => engine.enabled)
    .map(([id, engine]) => ({ id, ...engine }));

  if (steps.length === 0) {
    return (
      <div className="factory-pipeline-empty">
        No active engines selected.
      </div>
    );
  }

  return (
    <div className="factory-progress-steps">
      {steps.map((step, index) => {
        const isActive = activeStep === step.id;
        const isDone = step.status === 'done';

        return (
          <div
            key={step.id}
            className={
              isActive
                ? 'factory-progress-step active'
                : isDone
                  ? 'factory-progress-step done'
                  : 'factory-progress-step'
            }
          >
            <div className="factory-progress-node">
              {index + 1}
            </div>

            <div className="factory-progress-info">
              <strong>{step.label}</strong>
              <span>{step.model}</span>

              <div className="factory-progress-bar">
                <div style={{ width: `${step.progress}%` }} />
              </div>
            </div>

            <Tag className={isDone ? 'tag-custom tag-green' : isActive ? 'tag-custom tag-cyan' : 'tag-custom tag-blue'}>
              {isDone ? 'Done' : isActive ? 'Active' : 'Queued'}
            </Tag>
          </div>
        );
      })}
    </div>
  );
};

class ContentFactoryPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      prompt: '',
      description: '',
      contentType: 'Post',
      tone: 'Professional',
      language: 'EN',
      include: ['Hashtags', 'CTA'],

      profiles: [
        { id: 'instagram', platform: 'Instagram', handle: '@aivis_official', status: 'connected' },
        { id: 'telegram', platform: 'Telegram', handle: '@aivis_news', status: 'connected' },
        { id: 'vk', platform: 'VK', handle: 'vk.com/aivis_group', status: 'disconnected' },
        { id: 'fanvue', platform: 'FanVue', handle: '@aivis_creator', status: 'disconnected' }
      ],
      selectedProfiles: ['instagram', 'telegram'],
      profileToAdd: undefined,

      engines: {
        text: {
          enabled: true,
          label: 'Text Generation',
          model: 'GPT-4o',
          status: 'ready',
          progress: 0
        },
        image: {
          enabled: true,
          label: 'Image Generation',
          model: 'DALL-E 3',
          status: 'ready',
          progress: 0
        },
        voice: {
          enabled: false,
          label: 'Voice Synthesis',
          model: 'ElevenLabs',
          status: 'skipped',
          progress: 0
        },
        optimize: {
          enabled: true,
          label: 'Optimization',
          model: 'GPT-4o',
          status: 'ready',
          progress: 0
        }
      },

      scheduleDraft: {
        profileId: 'instagram',
        title: '',
        date: null,
        time: null
      },
      scheduledPosts: [
        {
          id: 1,
          profileId: 'instagram',
          title: 'New AI features showcase',
          date: '2025-05-25',
          time: '14:30',
          status: 'scheduled'
        },
        {
          id: 2,
          profileId: 'telegram',
          title: 'Weekly update & news',
          date: '2025-05-25',
          time: '16:00',
          status: 'scheduled'
        }
      ],

      generatedPosts: [],
      history: [],
      isGenerating: false,
      activeStep: null
    };
  }

  handleParamChange = (key, value) => {
    this.setState({ [key]: value });
  };

  handleIncludeToggle = (value) => {
    this.setState(prev => ({
      include: prev.include.includes(value)
        ? prev.include.filter(item => item !== value)
        : [...prev.include, value]
    }));
  };

  toggleEngine = (engineId) => {
    this.setState(prev => {
      const current = prev.engines[engineId];
      const nextEnabled = !current.enabled;

      return {
        engines: {
          ...prev.engines,
          [engineId]: {
            ...current,
            enabled: nextEnabled,
            status: nextEnabled ? 'ready' : 'skipped',
            progress: 0
          }
        }
      };
    });
  };

  changeEngineModel = (engineId, model) => {
    this.setState(prev => ({
      engines: {
        ...prev.engines,
        [engineId]: {
          ...prev.engines[engineId],
          model
        }
      }
    }));
  };

  connectProfile = (profileId) => {
    this.setState(prev => ({
      profiles: prev.profiles.map(profile =>
        profile.id === profileId
          ? { ...profile, status: 'connected' }
          : profile
      ),
      selectedProfiles: prev.selectedProfiles.includes(profileId)
        ? prev.selectedProfiles
        : [...prev.selectedProfiles, profileId]
    }));
  };

  disconnectProfile = (profileId) => {
    this.setState(prev => ({
      profiles: prev.profiles.map(profile =>
        profile.id === profileId
          ? { ...profile, status: 'disconnected' }
          : profile
      ),
      selectedProfiles: prev.selectedProfiles.filter(id => id !== profileId)
    }));
  };

  removeProfile = (profileId) => {
    this.setState(prev => ({
      profiles: prev.profiles.filter(profile => profile.id !== profileId),
      selectedProfiles: prev.selectedProfiles.filter(id => id !== profileId),
      scheduledPosts: prev.scheduledPosts.filter(item => item.profileId !== profileId)
    }));
  };

  addProfile = () => {
    const { profileToAdd, profiles } = this.state;
    if (!profileToAdd) return;

    const baseId = profileToAdd.toLowerCase().replace(/[^a-z0-9]/g, '');
    const id = `${baseId}-${Date.now()}`;

    const alreadyExists = profiles.some(profile => profile.platform === profileToAdd);
    if (alreadyExists) {
      this.setState({ profileToAdd: undefined });
      return;
    }

    this.setState(prev => ({
      profiles: [
        ...prev.profiles,
        {
          id,
          platform: profileToAdd,
          handle: `@${baseId}_profile`,
          status: 'disconnected'
        }
      ],
      profileToAdd: undefined
    }));
  };

  toggleSelectedProfile = (profileId) => {
    const profile = this.state.profiles.find(item => item.id === profileId);
    if (!profile || profile.status !== 'connected') return;

    this.setState(prev => ({
      selectedProfiles: prev.selectedProfiles.includes(profileId)
        ? prev.selectedProfiles.filter(id => id !== profileId)
        : [...prev.selectedProfiles, profileId]
    }));
  };

  handleScheduleDraftChange = (key, value) => {
    this.setState(prev => ({
      scheduleDraft: {
        ...prev.scheduleDraft,
        [key]: value
      }
    }));
  };

  formatDateValue = (value) => {
    if (!value) return '';

    if (typeof value === 'string') return value;

    if (value.format) {
      return value.format('YYYY-MM-DD');
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toISOString().slice(0, 10);
  };

  formatTimeValue = (value) => {
    if (!value) return '';

    if (typeof value === 'string') return value;

    if (value.format) {
      return value.format('HH:mm');
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  addScheduleItem = () => {
    const { scheduleDraft } = this.state;
    if (!scheduleDraft.profileId || !scheduleDraft.title || !scheduleDraft.date || !scheduleDraft.time) return;

    const dateValue = this.formatDateValue(scheduleDraft.date);
    const timeValue = this.formatTimeValue(scheduleDraft.time);

    this.setState(prev => ({
      scheduledPosts: [
        {
          id: Date.now(),
          profileId: scheduleDraft.profileId,
          title: scheduleDraft.title,
          date: dateValue,
          time: timeValue,
          status: 'scheduled'
        },
        ...prev.scheduledPosts
      ],
      scheduleDraft: {
        ...prev.scheduleDraft,
        title: '',
        date: null,
        time: null
      }
    }));
  };

  removeScheduleItem = (id) => {
    this.setState(prev => ({
      scheduledPosts: prev.scheduledPosts.filter(item => item.id !== id)
    }));
  };

  handleGenerate = () => {
    const { prompt, selectedProfiles, engines, isGenerating, contentType } = this.state;
    if (!prompt.trim() || selectedProfiles.length === 0 || isGenerating) return;

    const activeEngineIds = Object.entries(engines)
      .filter(([, engine]) => engine.enabled)
      .map(([id]) => id);

    if (activeEngineIds.length === 0) return;

    const resetEngines = Object.fromEntries(
      Object.entries(engines).map(([id, engine]) => [
        id,
        {
          ...engine,
          status: engine.enabled ? 'queued' : 'skipped',
          progress: 0
        }
      ])
    );

    this.setState({
      isGenerating: true,
      activeStep: activeEngineIds[0],
      engines: resetEngines
    });

    let stepIndex = 0;

    const runStep = () => {
      const engineId = activeEngineIds[stepIndex];

      if (!engineId) {
        const post = {
          id: Date.now(),
          title: `${contentType} generated`,
          text: `Preview: ${prompt.slice(0, 140)}${prompt.length > 140 ? '...' : ''}`,
          status: 'Draft',
          createdAt: new Date().toLocaleString(),
          enginesUsed: activeEngineIds.length
        };

        this.setState(prev => ({
          isGenerating: false,
          activeStep: null,
          generatedPosts: [post, ...prev.generatedPosts],
          history: [
            {
              id: `history-${post.id}`,
              status: 'Completed',
              text: `${contentType} generated through ${activeEngineIds.length} engine(s)`,
              time: new Date().toLocaleString()
            },
            ...prev.history
          ]
        }));

        return;
      }

      this.setState(prev => ({
        activeStep: engineId,
        engines: {
          ...prev.engines,
          [engineId]: {
            ...prev.engines[engineId],
            status: 'active',
            progress: 12
          }
        }
      }));

      let progress = 12;

      const interval = setInterval(() => {
        progress += 16;

        this.setState(prev => ({
          engines: {
            ...prev.engines,
            [engineId]: {
              ...prev.engines[engineId],
              progress: Math.min(progress, 100)
            }
          }
        }));

        if (progress >= 100) {
          clearInterval(interval);

          this.setState(prev => ({
            engines: {
              ...prev.engines,
              [engineId]: {
                ...prev.engines[engineId],
                status: 'done',
                progress: 100
              }
            }
          }));

          stepIndex += 1;
          setTimeout(runStep, 250);
        }
      }, 180);
    };

    runStep();
  };

  renderCreateTab = () => {
    const { prompt, description, contentType, tone, language, include } = this.state;

    return (
      <Space vertical align="start" style={{ width: '100%' }}>
        <Text className="factory-label">Content Idea / Prompt</Text>
        <TextArea
          value={prompt}
          onChange={(v) => this.handleParamChange('prompt', v)}
          placeholder="Enter your content idea, topic, campaign concept..."
          autosize={{ minRows: 4, maxRows: 6 }}
          className="factory-input factory-textarea"
        />

        <Text className="factory-label">Description</Text>
        <Input
          value={description}
          onChange={(v) => this.handleParamChange('description', v)}
          placeholder="Audience, goal, context, product notes..."
          className="factory-input"
        />

        <Row gutter={[12, 12]} style={{ width: '100%' }}>
          <Col xs={24} md={12}>
            <OptionTiles
              label="Content Type"
              value={contentType}
              options={['Post', 'Article', 'Thread', 'Story']}
              onChange={(v) => this.handleParamChange('contentType', v)}
            />
          </Col>

          <Col xs={24} md={12}>
            <OptionTiles
              label="Tone"
              value={tone}
              options={['Professional', 'Casual', 'Promo', 'Bold']}
              onChange={(v) => this.handleParamChange('tone', v)}
            />
          </Col>

          <Col xs={24} md={12}>
            <OptionTiles
              label="Language"
              value={language}
              options={['EN', 'RU', 'ES', 'FR']}
              onChange={(v) => this.handleParamChange('language', v)}
            />
          </Col>

          <Col xs={24} md={12}>
            <IncludeTiles
              values={include}
              onToggle={this.handleIncludeToggle}
            />
          </Col>
        </Row>
      </Space>
    );
  };

  renderEnginesTab = () => {
    const { engines, activeStep } = this.state;

    return (
      <div>
        <div className="factory-engines-list">
          {Object.entries(engines).map(([id, engine]) => (
            <EngineRow
              key={id}
              id={id}
              engine={engine}
              models={AI_MODEL_OPTIONS[id]}
              onToggle={this.toggleEngine}
              onModelChange={this.changeEngineModel}
            />
          ))}
        </div>
      </div>
    );
  };

  renderSchedulerTab = () => {
    const { scheduleDraft, scheduledPosts, profiles } = this.state;
    const connectedProfiles = profiles.filter(profile => profile.status === 'connected');

    return (
      <div>
        <div className="factory-schedule-form">
          <Select
            value={scheduleDraft.profileId}
            onChange={(v) => this.handleScheduleDraftChange('profileId', v)}
            className="factory-select"
            style={{ width: '100%' }}
          >
            {connectedProfiles.map(profile => (
              <Select.Option key={profile.id} value={profile.id}>
                {profile.platform}
              </Select.Option>
            ))}
          </Select>

          <Input
            value={scheduleDraft.title}
            onChange={(v) => this.handleScheduleDraftChange('title', v)}
            placeholder="Publication title..."
            className="factory-input"
          />

          <DatePicker
            value={scheduleDraft.date}
            onChange={(v) => this.handleScheduleDraftChange('date', v)}
            className="factory-date"
            style={{ width: '100%' }}
          />

          <TimePicker
            value={scheduleDraft.time}
            onChange={(v) => this.handleScheduleDraftChange('time', v)}
            className="factory-date"
            style={{ width: '100%' }}
          />

          <Button className="factory-small-button primary" onClick={this.addScheduleItem}>
            Add
          </Button>
        </div>

        <div className="factory-schedule-table">
          <div className="factory-schedule-head">
            <span>Profile</span>
            <span>Title</span>
            <span>Date</span>
            <span>Time</span>
            <span>Status</span>
            <span></span>
          </div>

          {scheduledPosts.length === 0 ? (
            <div className="factory-table-empty">No scheduled publications.</div>
          ) : (
            scheduledPosts.map(item => {
              const profile = profiles.find(p => p.id === item.profileId);

              return (
                <div className="factory-schedule-row" key={item.id}>
                  <span>{profile?.platform || '-'}</span>
                  <strong>{item.title}</strong>
                  <span>{item.date}</span>
                  <span>{item.time}</span>
                  <Tag className="tag-custom tag-blue">{item.status}</Tag>
                  <Button
                    className="factory-table-delete"
                    onClick={() => this.removeScheduleItem(item.id)}
                  >
                    ×
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  renderProfilesTab = () => {
    const { profiles, selectedProfiles, profileToAdd } = this.state;

    return (
      <div>
        <div className="factory-profile-list">
          {profiles.map(profile => {
            const connected = profile.status === 'connected';
            const selected = selectedProfiles.includes(profile.id);

            return (
              <div
                key={profile.id}
                className={
                  connected
                    ? selected
                      ? 'factory-profile-row connected selected'
                      : 'factory-profile-row connected'
                    : 'factory-profile-row disconnected'
                }
              >
                <button
                  type="button"
                  className="factory-profile-select"
                  onClick={() => this.toggleSelectedProfile(profile.id)}
                  disabled={!connected}
                >
                  {selected ? '✓' : ''}
                </button>

                <div className="factory-profile-info">
                  <strong>{profile.platform}</strong>
                  <span>{profile.handle}</span>
                </div>

                <Tag className={connected ? 'tag-custom tag-green' : 'tag-custom tag-red'}>
                  {connected ? 'Connected' : 'Offline'}
                </Tag>

                {connected ? (
                  <Button
                    className="factory-small-button"
                    onClick={() => this.disconnectProfile(profile.id)}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    className="factory-small-button primary"
                    onClick={() => this.connectProfile(profile.id)}
                  >
                    Connect
                  </Button>
                )}

                <Button
                  className="factory-small-button danger"
                  onClick={() => this.removeProfile(profile.id)}
                >
                  Delete
                </Button>
              </div>
            );
          })}
        </div>

        <div className="factory-add-profile">
          <Select
            placeholder="Select platform"
            value={profileToAdd}
            onChange={(v) => this.handleParamChange('profileToAdd', v)}
            className="factory-select"
            style={{ width: '100%' }}
          >
            {PROFILE_PLATFORMS.map(platform => (
              <Select.Option key={platform} value={platform}>
                {platform}
              </Select.Option>
            ))}
          </Select>

          <Button className="factory-small-button primary" onClick={this.addProfile}>
            Add Profile
          </Button>
        </div>
      </div>
    );
  };

  renderOutputTab = () => {
    const { generatedPosts } = this.state;

    if (generatedPosts.length === 0) {
      return (
        <div className="factory-empty-box">
          <Empty description="No generated content yet." />
        </div>
      );
    }

    return (
      <div className="factory-output-list">
        {generatedPosts.map(post => (
          <div className="factory-post-card" key={post.id}>
            <div className="factory-post-head">
              <IconAIStrokedLevel1 />
              <div>
                <strong>{post.title}</strong>
                <span>{post.createdAt}</span>
              </div>
              <Tag className="tag-custom tag-amber">{post.status}</Tag>
            </div>

            <p>{post.text}</p>

            <div className="factory-post-tags">
              <Tag className="tag-custom tag-cyan">{post.enginesUsed} Engines</Tag>
              <Tag className="tag-custom tag-blue">Draft</Tag>
              <Tag className="tag-custom tag-green">Ready</Tag>
            </div>

            <div className="factory-post-actions">
              <Button className="factory-small-button">Edit</Button>
              <Button className="factory-small-button">Schedule</Button>
              <Button className="factory-small-button primary">Publish</Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  renderHistoryPanel = () => {
    const { history, isGenerating } = this.state;

    return (
      <Card className="info-card factory-panel factory-history-panel" title="Recent Activity">
        {history.length === 0 ? (
          <div className="factory-empty-state">
            No production activity yet.
          </div>
        ) : (
          <div className="factory-history-list">
            {history.map(item => (
              <div className="factory-history-item" key={item.id}>
                <IconCheckCircleStroked />
                <strong>{item.text}</strong>
                <span>{item.time}</span>
                <Tag className="tag-custom tag-green">{item.status}</Tag>
              </div>
            ))}
          </div>
        )}

        <div className="factory-status-line">
          Status:{' '}
          <Tag className={isGenerating ? 'tag-custom tag-amber' : 'tag-custom tag-green'}>
            {isGenerating ? 'Generating' : 'Ready'}
          </Tag>
        </div>
      </Card>
    );
  };

  renderOverviewPanel = () => {
    const { generatedPosts, scheduledPosts, selectedProfiles, engines } = this.state;
    const activeEngines = Object.values(engines).filter(engine => engine.enabled).length;

    return (
      <Card className="info-card factory-panel factory-overview-panel" title="Factory Overview">
        <div className="factory-overview-grid">
          <div>
            <strong>{generatedPosts.length}</strong>
            <span>Generated</span>
          </div>

          <div>
            <strong>{scheduledPosts.length}</strong>
            <span>Scheduled</span>
          </div>

          <div>
            <strong>{selectedProfiles.length}</strong>
            <span>Profiles</span>
          </div>

          <div>
            <strong>{activeEngines}</strong>
            <span>Engines</span>
          </div>
        </div>
      </Card>
    );
  };

  render() {
    const { isGenerating, prompt } = this.state;

    return (
      <div className="grid-container factory-page">
        <div className="page-header">
          <Text className="page-title">Content Factory</Text>
        </div>

        <Row gutter={[16, 16]} className="factory-dashboard">
          <Col span={24}>
            <Card className="info-card factory-hero">
              <div className="factory-hero-content">
                <Typography.Title heading={4} className="factory-title">
                  AI Content Production Console
                </Typography.Title>
                <Text className="textBlock">
                  Create, optimize, schedule and distribute multi-platform content through a controlled AI pipeline.
                </Text>
              </div>

              <div className="factory-hero-actions">
                <Button className="factory-preset-button" onClick={() => this.handleParamChange('prompt', 'Create a product launch post for a futuristic AI service.')}>
                  Quick Post
                </Button>
                <Button className="factory-preset-button" onClick={() => this.handleParamChange('prompt', 'Build a weekly AI content campaign with platform-specific variations.')}>
                  AI Campaign
                </Button>
                <Button className="factory-preset-button" onClick={() => this.handleParamChange('prompt', 'Repurpose this idea into posts, captions, visual prompts and short scripts.')}>
                  Repurpose
                </Button>
              </div>

              <img src="/assets/fabric_ai_logo.png" alt="" className="factory-hero-art" />
            </Card>
          </Col>

          <Col xs={24} xl={8}>
            <Card className="info-card factory-panel factory-workbench-panel" title="Factory Workbench">
              <Tabs type="line" className="factory-tabs">
                <TabPane tab="Create" itemKey="create">
                  {this.renderCreateTab()}
                </TabPane>

                <TabPane tab="Engines" itemKey="engines">
                  {this.renderEnginesTab()}
                </TabPane>

                <TabPane tab="Profiles" itemKey="profiles">
                  {this.renderProfilesTab()}
                </TabPane>
              </Tabs>

              <Button
                className="button-custom factory-generate-button"
                icon={<IconSendStroked />}
                onClick={this.handleGenerate}
                loading={isGenerating}
                disabled={!prompt.trim() || isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Generate Content'}
              </Button>
            </Card>

            {this.renderOverviewPanel()}
          </Col>

          <Col xs={24} xl={8}>
            <Card className="info-card factory-panel factory-pipeline-panel" title="Pipeline Progress">
              <PipelineProgress
                engines={this.state.engines}
                activeStep={this.state.activeStep}
              />
            </Card>

            <Card className="info-card factory-panel factory-output-panel" title="Generated Content">
              {this.renderOutputTab()}
            </Card>
          </Col>

          <Col xs={24} xl={8}>
            <Card className="info-card factory-panel factory-scheduler-panel" title="Publication Scheduler">
              {this.renderSchedulerTab()}
            </Card>

            {this.renderHistoryPanel()}
          </Col>
        </Row>
      </div>
    );
  }
}

export default ContentFactoryPage;